import { Close } from '@carbon/icons-react';
import { useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { HorizBox, PadBox, Separator, VertBox } from 'component/basics';
import { BreadCrumb } from 'component/button';
import { Heading, UtilityText } from 'component/text';
import { FocusLoopView } from 'platform-specific/focustrap';
import { DocumentLevelComponent } from 'platform-specific/popup';
import React from 'react';
import { Pressable, ScrollView, useWindowDimensions } from 'react-native';
import type { FormatParams, TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';

const MAX_MODAL_HEIGHT = 680;
const MAX_MODAL_WIDTH = 600;
const HORIZONTAL_PADDING = 20;

function useMaxHeight() {
    const { height } = useWindowDimensions();
    return Math.min(height, MAX_MODAL_HEIGHT);
}

function BaseModalTopClose({ onClose }: { onClose: () => void }) {
    const { colors } = useTheme();
    return (
        <>
            <PadBox horiz={20} vert={8}>
                <HorizBox center right wide>
                    <BreadCrumb
                        ariaLabel={t('modal.close_button', 'Close')}
                        icon={Close}
                        iconProps={{ size: 32, color: colors.colorTextPrimary }}
                        onPress={onClose}
                        testID='close-modal'
                    />
                </HorizBox>
            </PadBox>
            <Separator />
        </>
    );
}

type BaseModalHeadingProps = {
    label: TLabel;
    subheadingLabel?: TLabel;
    subheadingFormatParams?: FormatParams;
    headingFormatParams?: FormatParams;
    subheadingContent?: React.ReactNode;
};

function BaseModalHeading({
    label,
    subheadingLabel,
    subheadingFormatParams,
    headingFormatParams,
    subheadingContent,
}: BaseModalHeadingProps) {
    const { colors } = useTheme();
    return (
        <VertBox gap={8}>
            <Heading formatParams={headingFormatParams} label={label} type='large' weight='medium' />
            {subheadingLabel && (
                <UtilityText color={colors.colorTextSecondary} formatParams={subheadingFormatParams} label={subheadingLabel} type='large' />
            )}
            {subheadingContent}
        </VertBox>
    );
}

export type BaseModalProps = {
    children: React.ReactNode;
    footer?: React.ReactNode;
    onClose: () => void;
    showTopClose?: boolean;
};

export function BaseModal({ children, footer, onClose, showTopClose }: BaseModalProps) {
    const s = useModalStyles();
    const maxHeight = useMaxHeight();
    const { width: screenWidth } = useWindowDimensions();
    // Max width of 600px but include 20px padding on each side
    const width = Math.min(MAX_MODAL_WIDTH + HORIZONTAL_PADDING * 2, screenWidth) - HORIZONTAL_PADDING * 2;
    return (
        <DocumentLevelComponent>
            <Pressable onPress={onClose} style={s.outer} tabIndex={-1} testID='popup-content'>
                <FocusLoopView index={1} onCloseLoop={() => onClose()}>
                    <Pressable onPress={(e) => e.stopPropagation()} style={[s.inner, { maxHeight, width }]} tabIndex={-1}>
                        {showTopClose && <BaseModalTopClose onClose={onClose} />}
                        <ScrollView>{children}</ScrollView>
                        {footer}
                    </Pressable>
                </FocusLoopView>
            </Pressable>
        </DocumentLevelComponent>
    );
}

BaseModal.Heading = BaseModalHeading;

const useModalStyles = createThemedStyles((tokens) => ({
    outer: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 40,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
    inner: {
        borderRadius: 12,
        flexShrink: 1,
        backgroundColor: tokens.colors.colorSurfacePrimary,
        boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.10)' as any, // FIXME: boxShadow not typed for native
        cursor: 'auto',
        overflow: 'hidden',
    },
}));
